import { Container, Graphics } from "pixi.js";
import type { GridSymbolPool, SymbolView } from "@slot/presentation";

/** Where a rolling cell sits and how its window and lanes are sized: design units, the grid root's space. */
export interface CellDrumGeometry {
  /** The cell's centre. */
  x: number;
  y: number;
  /** The window the rolling symbols show through, centred on the cell. */
  windowWidth: number;
  windowHeight: number;
  /**
   * The distance between two lanes. At rest the lanes next to the result must be out of the window: the spacing is at
   * least half the window plus half the tallest symbol, or a sliver of a neighbour shows at the window's edge.
   */
  laneSpacing: number;
}

/** How the result settles after its impact. */
export interface CellDrumLanding {
  symbolId: string;
  /** How far past the cell the result travels before it settles back, in lane spacings. */
  overshootLanes: number;
  settleSeconds: number;
  settleEase: (t: number) => number;
  /** How far above the cell, in lane spacings, the result leaves the window, finishing its stop unmasked (decision 53). */
  unmaskLanes: number;
}

/** What the drum reports from one frame: its impact (the bottom of the overshoot) and its rest. */
export interface CellDrumStep {
  impact: boolean;
  done: boolean;
}

/** Five lanes: the window's, two above (the result binds to one of them, out of sight) and two below. */
const LANE_COUNT = 5;
const LANE_SPAN = LANE_COUNT / 2;

type Phase = "roll" | "brake" | "settle" | "done";

interface Lane {
  view: SymbolView;
  /** The lane's place in the strip when the drum has not moved, in lane spacings from the centre. */
  base: number;
  /** How many times it has wrapped from the bottom to the top. */
  cycle: number;
}

/**
 * One cell rolling in its own window (internal to the hold mechanic, decision 50): a strip of pooled symbol views
 * scrolling down through a mask the size of the window, each lane that leaves the bottom wrapping back to the top with
 * a fresh filler. The motion is a function of the mechanic's logical time, so turbo, pause and frame-step need no
 * scaling here: a linear spin-up to full speed, then a uniform deceleration from full speed onto the result, past the
 * cell by the overshoot, and a settle back.
 *
 * The strip is a lattice: the lanes sit one spacing apart and share one phase, so a lane SLOT (its place counted from
 * the centre, unwrapped) names where a symbol will be at any time. `planLanding` solves when to start braking so that
 * the impact falls exactly on the mechanic's landing time — the brake's length follows from the speed and the distance
 * still to go, between one and two spacings — and binds the result to the slot that will stop on the cell, which is
 * out of the window when bound (at least a spacing above it). So cells land exactly on schedule, in order, and no
 * symbol is ever swapped in view. The view showing the cell when the roll starts becomes the centre lane: the roll
 * starts from what is on screen.
 */
export class CellDrum {
  public readonly index: number;
  private readonly host: Container;
  private readonly pool: GridSymbolPool;
  private readonly geometry: CellDrumGeometry;
  private readonly container: Container;
  private readonly lanes: Lane[] = [];
  private readonly pickFiller: () => string;
  private readonly fillerStretchY: number;

  private phase: Phase = "roll";
  private startTime = 0;
  private fullSpeed = 0;
  private spinUpSeconds = 0;
  private offset = 0;

  /** The planned stop: the slot that stops on the cell, the result, the brake's start and length. */
  private plan: { slot: number; landing: CellDrumLanding; commitAt: number; brakeSeconds: number } | null = null;
  private finalLane: Lane | null = null;
  /** The result's lane has left the window for the host (decision 53): it finishes its stop over the window's edges. */
  private released = false;
  private brakeStart = 0;
  private brakeFrom = 0;
  private brakeDistance = 0;
  private brakeSeconds = 0;
  private overshoot = 0;

  public constructor(input: {
    index: number;
    host: Container;
    pool: GridSymbolPool;
    geometry: CellDrumGeometry;
    /** The view showing the cell now, if any: it becomes the centre lane and scrolls away first. */
    current: SymbolView | null;
    pickFiller: () => string;
    fillerStretchY: number;
  }) {
    this.index = input.index;
    this.host = input.host;
    this.pool = input.pool;
    this.geometry = input.geometry;
    this.pickFiller = input.pickFiller;
    this.fillerStretchY = input.fillerStretchY;

    const { x, y, windowWidth, windowHeight } = input.geometry;
    this.container = new Container({ label: `HoldCell${input.index}` });
    this.container.position.set(x, y);
    const mask = new Graphics().rect(-windowWidth / 2, -windowHeight / 2, windowWidth, windowHeight).fill(0xffffff);
    mask.label = "HoldCellWindow";
    this.container.addChild(mask);
    this.container.mask = mask;
    this.host.addChild(this.container);

    for (let k = 0; k < LANE_COUNT; k++) {
      const base = k - (LANE_COUNT - 1) / 2;
      let view: SymbolView;
      if (base === 0 && input.current) {
        view = input.current;
      } else {
        view = this.pool.acquire();
        this.bindFiller(view);
      }
      this.container.addChild(view.node);
      view.node.visible = true;
      this.lanes.push({ view, base, cycle: 0 });
    }
    this.layoutLanes();
  }

  public get isRolling(): boolean {
    return this.phase === "roll";
  }

  /** The roll's start, in the mechanic's logical seconds. */
  public get rollStartTime(): number {
    return this.startTime;
  }

  /** The view the result is bound to, once its lane is in place. */
  public get finalView(): SymbolView | null {
    return this.finalLane?.view ?? null;
  }

  public start(now: number, input: { fullSpeed: number; spinUpSeconds: number }): void {
    this.startTime = now;
    this.fullSpeed = input.fullSpeed;
    this.spinUpSeconds = Math.max(0, input.spinUpSeconds);
  }

  /** The natural brake's shortest and longest length (the distance to go is one to two spacings plus the overshoot). */
  public brakeWindow(overshootLanes: number): { min: number; max: number } {
    const s = this.geometry.laneSpacing;
    const v = Math.max(this.fullSpeed, 1e-6);
    const ov = overshootLanes * s;
    return { min: (2 * (s + ov)) / v, max: (2 * (2 * s + ov)) / v };
  }

  /**
   * Plans the stop so the impact falls at `landAt` (no earlier than a full brake from `now`): the brake starts at full
   * speed and decelerates uniformly onto the slot that will then sit `overshoot` below the cell. The result binds to
   * that slot's lane now if the lane is already in place (it is out of the window), else the moment the lane wraps
   * into the slot at the top.
   */
  public planLanding(now: number, landAt: number, landing: CellDrumLanding): void {
    if (this.phase !== "roll") return;
    const s = this.geometry.laneSpacing;
    const v = this.fullSpeed;
    const ov = landing.overshootLanes * s;
    const at = Math.max(landAt, now + this.brakeWindow(landing.overshootLanes).max, this.startTime + this.spinUpSeconds + this.brakeWindow(landing.overshootLanes).max);
    // The impact leaves the stopping slot `ov` below the centre: o(commit) + v·B/2 ≡ ov (mod s), with o(commit) =
    // o(at) − v·B at full speed, so v·B/2 ≡ o(at) − ov (mod s), taken in [s + ov, 2s + ov).
    const residue = this.fullSpeedOffsetAt(at) - ov;
    const half = s + ov + mod(residue - s - ov, s);
    const brakeSeconds = (2 * half) / v;
    const commitAt = at - brakeSeconds;
    const distanceAtCommit = half - ov;
    const slot = Math.round((-distanceAtCommit - this.fullSpeedOffsetAt(commitAt)) / s);
    this.unbindPlannedFinal();
    this.plan = { slot, landing, commitAt, brakeSeconds };
    this.bindFinalIfInPlace();
  }

  /**
   * The slam: brake now, over `brakeSeconds`, onto the first slot at least a spacing above the cell (out of the window),
   * whatever speed that takes. A stop already planned is dropped (its lane, still out of sight, takes a filler).
   */
  public slam(now: number, brakeSeconds: number, landing: CellDrumLanding): void {
    if (this.phase !== "roll") return;
    const s = this.geometry.laneSpacing;
    this.offset = this.rollOffsetAt(now);
    const slot = Math.floor(-1 - this.offset / s);
    this.unbindPlannedFinal();
    this.plan = { slot, landing, commitAt: now, brakeSeconds };
    this.layoutLanes();
    this.bindFinalIfInPlace();
    this.beginBrake(now, -(slot * s + this.offset) + landing.overshootLanes * s, brakeSeconds, this.offset);
  }

  /** Advances the drum to `now`: the lanes move, wrapped lanes take new fillers (or the result); reports the impact and the rest. */
  public tick(now: number): CellDrumStep {
    const step: CellDrumStep = { impact: false, done: false };
    if (this.phase === "roll" && this.plan && now >= this.plan.commitAt) {
      // The brake starts at its planned time, a frame ago at most: the motion is a function of time, so it catches up.
      // The strip is laid out at that time first: in a fast mode the stopping slot's lane may have wrapped in since.
      const s = this.geometry.laneSpacing;
      const from = this.fullSpeedOffsetAt(this.plan.commitAt);
      this.offset = from;
      this.layoutLanes();
      this.beginBrake(this.plan.commitAt, -(this.plan.slot * s + from) + this.plan.landing.overshootLanes * s, this.plan.brakeSeconds, from);
    }
    switch (this.phase) {
      case "roll":
        this.offset = this.rollOffsetAt(now);
        break;
      case "brake": {
        const u = this.brakeSeconds > 0 ? Math.min(1, (now - this.brakeStart) / this.brakeSeconds) : 1;
        this.offset = this.brakeFrom + this.brakeDistance * (2 * u - u * u);
        if (u >= 1) {
          step.impact = true;
          this.phase = "settle";
          if (this.plan!.landing.settleSeconds <= 0 || this.overshoot === 0) this.finish(step);
        }
        break;
      }
      case "settle": {
        const landing = this.plan!.landing;
        const u = Math.min(1, (now - (this.brakeStart + this.brakeSeconds)) / landing.settleSeconds);
        this.offset = this.brakeFrom + this.brakeDistance - this.overshoot * landing.settleEase(u);
        if (u >= 1) this.finish(step);
        break;
      }
      case "done":
        return step;
    }
    this.releaseFinalIfNear();
    this.layoutLanes();
    return step;
  }

  /**
   * The landed view back into the grid root at the cell's centre, the other lanes released, the window gone. The
   * caller binds the returned view as the cell.
   */
  public handOff(release: (view: SymbolView) => void): SymbolView {
    const final = this.finalLane;
    if (!final || this.phase !== "done") throw new Error(`CellDrum ${this.index}: handOff before the drum landed.`);
    this.host.addChild(final.view.node);
    final.view.node.position.set(this.geometry.x, this.geometry.y);
    final.view.node.scale.set(1, 1);
    this.teardown(release, final.view);
    return final.view;
  }

  /** Every lane released (none kept), the window gone: an abort, a recovery. */
  public discard(release: (view: SymbolView) => void): void {
    this.teardown(release, null);
  }

  private beginBrake(start: number, distance: number, seconds: number, from: number): void {
    if (!this.finalLane) throw new Error(`CellDrum ${this.index}: the brake started before the result's lane was in place.`);
    this.overshoot = this.plan!.landing.overshootLanes * this.geometry.laneSpacing;
    this.brakeStart = start;
    this.brakeFrom = from;
    this.brakeDistance = distance;
    this.brakeSeconds = seconds;
    this.phase = "brake";
  }

  private teardown(release: (view: SymbolView) => void, keep: SymbolView | null): void {
    for (const lane of this.lanes) {
      if (lane.view === keep) continue;
      // A pooled view stays where it is released: back to the grid root first, so destroying the window keeps it.
      this.host.addChild(lane.view.node);
      lane.view.node.scale.set(1, 1);
      release(lane.view);
    }
    this.lanes.length = 0;
    this.container.destroy({ children: true });
  }

  private finish(step: CellDrumStep): void {
    this.offset = this.brakeFrom + this.brakeDistance - this.overshoot;
    this.phase = "done";
    step.done = true;
  }

  /** Distance rolled `now`: a linear ramp to full speed over the spin-up, then full speed. */
  private rollOffsetAt(now: number): number {
    const t = Math.max(0, now - this.startTime);
    const ramp = this.spinUpSeconds;
    if (ramp <= 0) return this.fullSpeed * t;
    if (t < ramp) return (this.fullSpeed * t * t) / (2 * ramp);
    return this.fullSpeed * (t - ramp / 2);
  }

  /** The full-speed branch of the roll, extended to any time (the plan's arithmetic; the plan keeps it after the spin-up). */
  private fullSpeedOffsetAt(time: number): number {
    return this.fullSpeed * (time - this.startTime - this.spinUpSeconds / 2);
  }

  /** The slot a lane holds now: its place from the centre, unwrapped (y = slot · spacing + offset). */
  private slotOf(lane: Lane): number {
    return lane.base - lane.cycle * LANE_COUNT;
  }

  private layoutLanes(): void {
    const spacing = this.geometry.laneSpacing;
    const period = LANE_COUNT * spacing;
    for (const lane of this.lanes) {
      const cycle = Math.floor((lane.base * spacing + this.offset + LANE_SPAN * spacing) / period);
      if (cycle !== lane.cycle) {
        lane.cycle = cycle;
        // A lane re-entering at the top takes a new filler, or the result when it enters the stopping slot.
        if (lane !== this.finalLane) this.bindFiller(lane.view);
        this.bindFinalIfInPlace();
      }
      const y = this.slotOf(lane) * spacing + this.offset;
      // The released result is in the host: the window's place added (the window is the cell's, unscaled).
      if (this.released && lane === this.finalLane) lane.view.node.position.set(this.geometry.x, this.geometry.y + y);
      else lane.view.node.position.set(0, y);
    }
  }

  /**
   * The result leaves the window once it is within `unmaskLanes` above the cell, braking or settling (decision 53): to
   * the host at the same place, so it finishes its stop over the window's edges.
   */
  private releaseFinalIfNear(): void {
    const final = this.finalLane;
    const reach = this.plan?.landing.unmaskLanes ?? 0;
    if (!final || this.released || reach <= 0 || (this.phase !== "brake" && this.phase !== "settle")) return;
    const s = this.geometry.laneSpacing;
    if (this.slotOf(final) * s + this.offset < -reach * s) return;
    this.host.addChild(final.view.node);
    this.released = true;
  }

  private bindFinalIfInPlace(): void {
    const plan = this.plan;
    if (!plan || this.finalLane) return;
    const lane = this.lanes.find((l) => this.slotOf(l) === plan.slot);
    if (!lane) return;
    this.finalLane = lane;
    this.pool.applySymbolIdToView(lane.view, plan.landing.symbolId, { pendingLand: true });
    lane.view.node.scale.set(1, 1);
  }

  private unbindPlannedFinal(): void {
    if (this.finalLane) this.bindFiller(this.finalLane.view);
    this.finalLane = null;
  }

  private bindFiller(view: SymbolView): void {
    this.pool.applySymbolIdToView(view, this.pickFiller(), { transient: true });
    view.node.scale.set(1, this.fillerStretchY);
  }
}

/** The non-negative remainder of `a` by `m`. */
function mod(a: number, m: number): number {
  return ((a % m) + m) % m;
}
